import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  NumberFormatter,
  ScrollArea,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconFocus2, IconMapPin, IconRouteAltLeft } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import type { NearestPropertiesTableProps, NearestPropertyRow } from './types';

/**
 * Presentational table for nearest-property results.
 *
 * Columns: color swatch (when show-all), property name, division, acres, miles, zoom-to button.
 * Row click → navigate to detail page.
 * Zoom button → zoom to extent on map.
 */
export function NearestPropertiesTable({
  rows,
  isLoading,
  error,
  maxRows = 10,
  onHoverRow,
  onZoomToRow,
  onNavigateRow,
  title = 'Nearest Properties',
  showAllLines = false,
  onToggleShowAllLines,
}: NearestPropertiesTableProps) {
  const displayRows = useMemo(()=>rows.slice(0, maxRows),[maxRows,rows]);

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent, row: NearestPropertyRow) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onNavigateRow?.(row);
      }
    },
    [onNavigateRow],
  );

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <NearestPropertiesSection title={title} showAllLines={showAllLines} onToggleShowAllLines={onToggleShowAllLines} rowCount={rows.length}>
        <Alert icon={<IconAlertCircle size={16} />} title="Error loading nearest properties" color="red" variant="light">
          {error}
        </Alert>
      </NearestPropertiesSection>
    );
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <NearestPropertiesSection title={title} showAllLines={showAllLines} onToggleShowAllLines={onToggleShowAllLines} rowCount={rows.length}>
        <Center mih={80}>
          <Loader size="sm" />
          <Text ml="sm" fz="sm" c="dimmed">Computing distances…</Text>
        </Center>
      </NearestPropertiesSection>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (rows.length === 0) {
    return (
      <NearestPropertiesSection title={title} showAllLines={showAllLines} onToggleShowAllLines={onToggleShowAllLines} rowCount={0}>
        <Center mih={60}>
          <Text fz="sm" c="dimmed">No nearby properties found</Text>
        </Center>
      </NearestPropertiesSection>
    );
  }

  // ── Table ──────────────────────────────────────────────────────────────────
  return (
    <NearestPropertiesSection title={title} showAllLines={showAllLines} onToggleShowAllLines={onToggleShowAllLines} rowCount={rows.length}>
      <ScrollArea.Autosize mah={300}>
        <Table highlightOnHover verticalSpacing={4} fz="xs" striped style={{ tableLayout: 'auto' }}>
          <Table.Thead>
            <Table.Tr>
              {showAllLines && <Table.Th style={{ width: 12, padding: '0 4px' }} />}
              <Table.Th>Property</Table.Th>
              <Table.Th>Division</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Acres</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Miles</Table.Th>
              <Table.Th style={{ width: 36 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayRows.map((row) => (
              <Table.Tr
                key={row.objectId}
                tabIndex={0}
                role="row"
                style={{ cursor: onNavigateRow ? 'pointer' : undefined }}
                onClick={() => onNavigateRow?.(row)}
                onKeyDown={(e) => handleRowKeyDown(e, row)}
                onMouseEnter={() => onHoverRow?.(row)}
                onMouseLeave={() => onHoverRow?.(null)}
                onFocus={() => onHoverRow?.(row)}
                onBlur={() => onHoverRow?.(null)}
              >
                {showAllLines && (
                  <Table.Td style={{ width: 12, padding: '0 4px' }}>
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: row.lineColor ?? 'transparent',
                        border: '1px solid rgba(0,0,0,0.15)',
                        flexShrink: 0,
                      }}
                    />
                  </Table.Td>
                )}
                <Table.Td>
                  <Text fw={500} fz="xs" lineClamp={1}>{row.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="xs" c="dimmed">{row.division ?? '—'}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  {row.acres != null ? (
                    <NumberFormatter value={row.acres} thousandSeparator decimalScale={0} />
                  ) : (
                    <Text fz="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  {row.distanceMiles != null ? (
                    <NumberFormatter value={row.distanceMiles} thousandSeparator decimalScale={1} suffix=" mi" />
                  ) : (
                    <Text fz="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td style={{ width: 36 }}>
                  {onZoomToRow && (
                    <Tooltip label="Zoom to property" withArrow>
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={(e) => {
                          e.stopPropagation();
                          onZoomToRow(row);
                        }}
                        aria-label={`Zoom to ${row.name}`}
                      >
                        <IconFocus2 size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>

      {rows.length > maxRows && (
        <Text fz={10} c="dimmed" ta="center" mt={4}>
          Showing {maxRows} of {rows.length} results
        </Text>
      )}
    </NearestPropertiesSection>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────────

function NearestPropertiesSection({
  title,
  children,
  showAllLines,
  onToggleShowAllLines,
  rowCount,
}: {
  title: string;
  children: React.ReactNode;
  showAllLines?: boolean;
  onToggleShowAllLines?: () => void;
  rowCount: number;
}) {
  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap={4}>
          <IconMapPin size={13} />
          <Text fz="xs" fw={600} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
            {title}
          </Text>
        </Group>
        {onToggleShowAllLines && rowCount > 0 && (
          <Button
            size="compact-xs"
            variant={showAllLines ? 'filled' : 'light'}
            color={showAllLines ? 'blue' : 'gray'}
            leftSection={<IconRouteAltLeft size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onToggleShowAllLines();
            }}
            aria-label="Toggle all distance lines"
          >
            {showAllLines ? 'Hide Lines' : 'Show Lines'}
          </Button>
        )}
      </Group>
      <Divider />
      {children}
    </Stack>
  );
}
